import { Checkbox } from '@/components/ui/Checkbox';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { FieldLabel } from '@/components/forms/FieldLabel';
import {
  FIELD_LABELS,
  SELF_ID_ANSWER_OPTIONS,
  UNDERREPRESENTED_CATEGORY_OPTIONS,
  type SelfIdAnswer,
} from '../constants';
import { parseSelfIdAnswer } from '../self-identification';

/**
 * The founding team's self-identification answers, held as one value because
 * the underrepresented answer and its categories are not independent: ticking
 * a category means "yes", and "no"/"would rather not say" means no categories.
 */
export interface SelfIdentificationValue {
  founderFemale?: SelfIdAnswer;
  founderUnderrepresented?: SelfIdAnswer;
  founderUnderrepresentedCategories: string[];
}

export interface FounderSelfIdentificationFieldsProps {
  value: SelfIdentificationValue;
  onChange: (next: SelfIdentificationValue) => void;
}

interface QuestionProps {
  question: string;
  answer?: SelfIdAnswer;
  onAnswer: (answer: SelfIdAnswer) => void;
}

/** One 3-option question (Yes / No / Would rather not say). */
function SelfIdQuestion({ question, answer, onAnswer }: QuestionProps) {
  return (
    <div className="flex flex-col gap-2">
      {/* Visible copy of the question; the radiogroup carries it as its name. */}
      <p className="text-sm text-navy">{question}</p>
      <SegmentedControl
        label={question}
        options={SELF_ID_ANSWER_OPTIONS}
        value={answer}
        size="sm"
        divided
        onChange={(next) => {
          const parsed = parseSelfIdAnswer(next);
          if (parsed) onAnswer(parsed);
        }}
      />
    </div>
  );
}

/**
 * Founder self-identification block (UX sheet r112-r115), shared by the create
 * wizard (Step 2) and the startup profile editor so the two never drift.
 * Controlled by value/onChange - it works with any form binding.
 *
 * The categories are always visible (no reveal toggle, r115).
 */
export function FounderSelfIdentificationFields({
  value,
  onChange,
}: FounderSelfIdentificationFieldsProps) {
  const categories = value.founderUnderrepresentedCategories;

  const answerUnderrepresented = (answer: SelfIdAnswer) =>
    onChange({
      ...value,
      founderUnderrepresented: answer,
      // Only a "yes" can carry categories.
      founderUnderrepresentedCategories: answer === 'yes' ? categories : [],
    });

  const toggleCategory = (category: string) => {
    const next = categories.includes(category)
      ? categories.filter((existing) => existing !== category)
      : [...categories, category];
    onChange({
      ...value,
      founderUnderrepresentedCategories: next,
      // Ticking a category answers the question; unticking the last one
      // leaves the answer alone rather than guessing it became "no".
      founderUnderrepresented: next.length > 0 ? 'yes' : value.founderUnderrepresented,
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <SelfIdQuestion
        question={FIELD_LABELS.founderFemaleQuestion}
        answer={value.founderFemale}
        onAnswer={(answer) => onChange({ ...value, founderFemale: answer })}
      />
      <div className="flex flex-col gap-3">
        <SelfIdQuestion
          question={FIELD_LABELS.founderUnderrepresentedQuestion}
          answer={value.founderUnderrepresented}
          onAnswer={answerUnderrepresented}
        />
        <fieldset className="flex flex-col gap-2">
          <FieldLabel label={FIELD_LABELS.founderUnderrepresentedCategories} variant="caps" />
          <div className="flex flex-wrap gap-4">
            {UNDERREPRESENTED_CATEGORY_OPTIONS.map((option) => (
              <Checkbox
                key={option.value}
                value={option.value}
                label={option.label}
                checked={categories.includes(option.value)}
                onChange={() => toggleCategory(option.value)}
              />
            ))}
          </div>
        </fieldset>
      </div>
    </div>
  );
}
